import { useState } from "react";
import { TypedFilter } from "odata-query";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IAutoTriggerFlow } from "@miplatform/types";

export const useAutoTriggerFlows = () => {
  const [autoTriggerFlows, setAutoTriggerFlows] = useState<IAutoTriggerFlow[]>(
    []
  );
  const [autoTriggerFlow, setAutoTriggerFlow] =
    useState<IAutoTriggerFlow | null>(null);
  const [filter, setFilter] = useState<TypedFilter<IAutoTriggerFlow>>({});

  const stateManager = useComponentState();

  const getAutoTriggerFlows = ({
    top,
    skip,
    filters: requestFilters,
    search = "",
    onSuccess,
    ...rest
  }: IApiRequest = {}) => {
    return stateManager.run(async () => {
      const filters = {
        ...requestFilters,
        ...filter,
        ...(search ? { name: { contains: search } } : {}),
      };

      const response = await taskService.autoTriggerFlows.getAll({
        top,
        skip,
        filters,
        sort: "name",
        ...rest,
      });

      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const getAutoTriggerFlowById = ({
    id,
    onSuccess,
  }: {
    id: string;
    onSuccess?: (data: IAutoTriggerFlow) => void;
  }) => {
    return stateManager.run(async () => {
      const response = await taskService.autoTriggerFlows.getById({ id });

      if (response.status === HTTP_STATUSES.OK) {
        setAutoTriggerFlow(response.data);
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const createAutoTriggerFlow = ({
    data,
    toastMessageConfig,
    onSuccess,
  }: IApiRequest & {
    onSuccess?: (data: IAutoTriggerFlow) => void;
  }) => {
    return stateManager.run(async () => {
      const response = await taskService.autoTriggerFlows.create({
        data,
        toastMessageConfig: toastMessageConfig || {
          success: { text: "Auto Trigger Flow created successfully" },
        },
      });

      if (
        response.status === HTTP_STATUSES.OK ||
        response.status === HTTP_STATUSES.CREATED
      ) {
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const updateAutoTriggerFlow = ({
    id,
    data,
    modifiedOn,
    toastMessageConfig,
    onSuccess,
  }: IApiRequest & {
    onSuccess?: (data: IAutoTriggerFlow) => void;
  }) => {
    return stateManager.run(async () => {
      const response = await taskService.autoTriggerFlows.update({
        id,
        data,
        modifiedOn,
        toastMessageConfig: toastMessageConfig || {
          success: { text: "Auto Trigger Flow updated successfully" },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const deleteAutoTriggerFlow = ({
    id,
    modifiedOn,
    toastMessageConfig,
    onSuccess,
  }: IApiRequest & {
    onSuccess?: (data: IAutoTriggerFlow) => void;
  }) => {
    return stateManager.run(async () => {
      const response = await taskService.autoTriggerFlows.delete({
        id,
        modifiedOn,
        toastMessageConfig: toastMessageConfig || {
          success: { text: "Auto Trigger Flow deleted successfully" },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const disableBuilder = ({
    id,
    builderId,
    data,
    toastMessageConfig,
    onSuccess,
  }: IApiRequest & {
    builderId: string;
    onSuccess?: (data: IAutoTriggerFlow) => void;
  }) => {
    return stateManager.run(async () => {
      const response = await taskService.autoTriggerFlows.disableBuilder({
        id,
        builderId,
        data,
        toastMessageConfig,
      });

      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const enableBuilder = ({
    id,
    builderId,
    data,
    toastMessageConfig,
    onSuccess,
  }: IApiRequest & {
    builderId: string;
    onSuccess?: (data: IAutoTriggerFlow) => void;
  }) => {
    return stateManager.run(async () => {
      const response = await taskService.autoTriggerFlows.enableBuilder({
        id,
        builderId,
        data,
        toastMessageConfig,
      });

      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    autoTriggerFlows,
    setAutoTriggerFlows,
    autoTriggerFlow,
    setAutoTriggerFlow,
    getAutoTriggerFlows,
    getAutoTriggerFlowById,
    createAutoTriggerFlow,
    updateAutoTriggerFlow,
    deleteAutoTriggerFlow,
    disableBuilder,
    enableBuilder,
    filter,
    setFilter,
  };
};
